import { revealReportCpr } from '@/lib/patientReports/cpr';
import { sniffCover } from '@/lib/patientReports/coverFile';
import { downloadPatientReportObject } from '@/lib/patientReports/docxFile';
import { docxHasToken, fillDocxTemplate } from '@/lib/patientReports/docxTemplate';
import { loadReportLetterhead } from '@/lib/patientReports/letterhead';
import type {
  PatientReportLetterheadChoice,
  PatientReportSettings,
} from '@/lib/patientReports/model';
import {
  CPR_PLACEHOLDER_TOKENS,
  buildPlaceholderDictionary,
  type PlaceholderFillContext,
} from '@/lib/patientReports/placeholders';
import { buildPatientReportDocument, type ReportCoverFile } from '@/lib/patientReports/reportPdf';

export async function materializeReportPdf(input: {
  settings: PatientReportSettings;
  clinic?: {
    name?: string | null;
    address?: unknown;
    cvr?: string | null;
    phone?: string | null;
    email?: string | null;
    branding?: import('@/types/database.generated').Json | null;
  } | null;
  letterhead: PatientReportLetterheadChoice;
  wordPath: string | null;
  coverPath: string | null;
  coverMime: string | null;
  includeCover: boolean;
  placeholders: PlaceholderFillContext;
  title: string;
  blocks: readonly {
    heading: string;
    body: string;
    pageBreakAfter?: boolean;
    headingStyle?: 'bold' | 'italic' | 'both';
    tocLines?: readonly { label: string; style?: 'bold' | 'italic' | 'both' }[];
    charts?: readonly { label: string; value: number; max: number }[];
  }[];
  patientId: string | null;
  canRevealCpr: boolean;
  /** Example preview. When set, a CPR token uses this value and is not revealed. */
  sampleCpr?: string | null;
}): Promise<Uint8Array> {
  const letterhead = await loadReportLetterhead(input.settings, input.clinic, input.letterhead);
  let context = input.placeholders;
  let docx: Uint8Array | null = null;
  if (input.wordPath) {
    const template = await downloadPatientReportObject(input.wordPath);
    const wantsCpr = CPR_PLACEHOLDER_TOKENS.some((token) => docxHasToken(template, token));
    if (wantsCpr) context = await withCpr(context, input);
    docx = fillDocxTemplate(template, buildPlaceholderDictionary(context));
  }
  const coverFile =
    input.includeCover && input.coverPath
      ? await downloadCoverFile(input.coverPath, input.coverMime)
      : null;
  return buildPatientReportDocument({
    letterhead,
    coverFile,
    coverText: input.includeCover ? { title: context.coverTitle, body: context.coverBody } : null,
    docx,
    title: input.title,
    blocks: input.blocks,
  });
}

async function withCpr(
  context: PlaceholderFillContext,
  input: {
    patientId: string | null;
    canRevealCpr: boolean;
    sampleCpr?: string | null;
  },
): Promise<PlaceholderFillContext> {
  if (input.sampleCpr != null) {
    return { ...context, cpr: input.sampleCpr, cprAccess: 'ready' };
  }
  if (!input.canRevealCpr) return { ...context, cpr: null, cprAccess: 'hidden' };
  if (context.cpr) return { ...context, cprAccess: 'ready' };
  if (!input.patientId) return { ...context, cpr: null, cprAccess: 'ready' };
  const cpr = await revealReportCpr(input.patientId);
  return { ...context, cpr, cprAccess: 'ready' };
}

export async function downloadCoverFile(
  path: string,
  mime: string | null,
): Promise<ReportCoverFile> {
  const bytes = await downloadPatientReportObject(path);
  const sniffed = sniffCover(bytes);
  if (!sniffed) throw new Error('cover-type');
  if (mime === 'application/pdf' || mime === 'image/png' || mime === 'image/jpeg') {
    if (mime !== sniffed.mime) throw new Error('cover-type');
  }
  return { bytes, mime: sniffed.mime };
}
